import {Trans} from '@lingui/macro';
import {flow} from 'lodash';
import memoize from 'lru-memoize';
import React, {FC} from 'react';
import styled, {css} from 'styled-components';

import {RendererOf} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {Icon} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {fontSizes, fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../../../../libs/shared/ui/src/styles/mixins';
import {UserThemeConsumer} from '../../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {ThemeContextType, UserTheme} from '../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {NodeTransform} from '../../../../../../srcCommon/formatters/markdown/markdownFormatterTypes';
import {MarkdownPlainFormatter} from '../../../../../../srcCommon/formatters/markdown/markdownPlainFormatter';
import {useAppSelector} from '../../../../../helpers/reduxHelpers';
import {AnnotationModel} from '../../../../../models/api/annotationModel';
import {CommentModel} from '../../../../../models/api/commentModel';
import {EmojiMap} from '../../../../../services/emojis/emojis';
import {getEmojiValidationMap} from '../../../../../store/stateless/emojiSelectors';
import {makeMarkdownTransformEmojis} from '../../../../common/formatters/markdown/markdownTransformEmojis';
import {makeMarkdownTransformMentions} from '../../../../common/formatters/markdown/markdownTransformMentions';
import {CellAuthorWrapper, CellAuthorWrapperProps} from './cellAuthorWrapper';

/*
 * Props.
 */

export interface CellCommentLineRenderSummaryData {
  props: CellCommentLineProps;
  emojiValidationMap: EmojiMap;
}

export interface CellCommentLineProps extends CellAuthorWrapperProps {
  text: string;
  annotation: AnnotationModel | undefined;
  mentionableAliases?: ReadonlySet<string>;
  isSelected?: boolean;
  isUnread?: boolean;
  hasFollowingComments?: boolean;
  shouldDisplayDraftStatus?: boolean;
  attachments?: CommentModel['attachments'];
  renderSummary: RendererOf<CellCommentLineRenderSummaryData>;
  shouldDisplayAttachmentIcon: boolean;
  shouldUseEllipsisOverflow: boolean;
  hasHighlight?: boolean;
}

/*
 * Style.
 */

// Here's a custom light blue color specifically for cell comments/precogs.
// If this needs to be used elsewhere, please standardize it within a colorStyles palette.
export const unreadCommentLightBlue = '#cce1ff';

interface CellCommentStyleProps {
  $isSelected: boolean;
  $isUnread: boolean;
  $isAnnotation: boolean;
  $hasFollowingComments: boolean;
  $hasHighlight?: boolean;
}

const StyledCommentDiv = styled.div<CellCommentStyleProps>`
  display: flex;
  padding: 0 6px;
  height: 20px;
  min-width: 0;

  background-color: ${(p) => getCellCommentBackgroundColor(p)};
  ${(p) => addBorderRadiusStyle(p.$hasFollowingComments)};
`;

function addBorderRadiusStyle(hasFollowingComments?: boolean) {
  if (hasFollowingComments) {
    return css`
      border-radius: 10px 2px 2px 10px;
    `;
  }

  return css`
    border-radius: 10px;
  `;
}

function addVerticallyCenteredSmallTextStyle() {
  return css`
    font-size: ${fontSizes.small};
    transform: translateY(0.5px);
    line-height: 19px;
  `;
}

// Returns a blue color in normal non-selected state; returns white in selected state.
function getNormallyBlueColor(theme: UserTheme, isSelected?: boolean) {
  return isSelected ? theme.invertedText : theme.palette.blue.shade40;
}

function getCellCommentBackgroundColor(props: CellCommentStyleProps & ThemeContextType) {
  if (props.$isSelected) {
    return props.theme.alphas.white30;
  }

  if (props.$isUnread) {
    return unreadCommentLightBlue;
  }

  return props.theme.alphas.gray20;
}

const StyledIconDiv = styled.div<CellCommentStyleProps>`
  padding: 2px 3px 2px 0;
  color: ${(p) => (p.$isSelected ? p.theme.invertedText : p.theme.greys.shade70)};
`;

const StyledDraftIconDiv = styled.div<CellCommentStyleProps>`
  padding: 4px 4px 0 2px;
  color: ${(p) => getNormallyBlueColor(p.theme, p.$isSelected)};
`;

const StyledDraftTextDiv = styled.div<CellCommentStyleProps>`
  display: inline-block;
  margin: 0 4px 0 0;
  color: ${(p) => getNormallyBlueColor(p.theme, p.$isSelected)};
  ${addVerticallyCenteredSmallTextStyle()};
  font-weight: ${fontWeights.semibold};
`;

const StyledSummaryDiv = styled.div<CellCommentStyleProps>`
  /** If we have a highlight, rely on <Highlight /> to use the correct colors. */
  color: ${(p) => (p.$isSelected && !p.$hasHighlight ? p.theme.invertedSubtext : p.theme.greys.shade80)};

  ${addVerticallyCenteredSmallTextStyle()};
  ${(p) => !p.$isAnnotation && ellipsis()};

  ${(p) =>
    p.$isAnnotation &&
    css`
      display: grid;
      grid-template-columns: repeat(3, auto);
      overflow: hidden;
    `};
`;

const StyledMoreDiv = styled.div<CellCommentStyleProps>`
  flex-shrink: 0;
  margin-left: 3px;

  display: flex;
  align-items: center;
  height: 20px;
  padding: 0 6px 0 4px;

  border-radius: 2px 10px 10px 2px;
  background-color: ${(p) => getCellCommentBackgroundColor(p)};

  color: ${(p) => getNormallyBlueColor(p.theme, p.$isSelected)};
`;

const StyledMoreTextDiv = styled.div`
  margin: 0 1px;

  font-weight: ${fontWeights.semibold};
  font-size: ${fontSizes.small};
  text-transform: lowercase;
`;

/*
 * Component.
 */

export const CellCommentLine: FC<CellCommentLineProps> = (props) => {
  const emojiValidationMap = useAppSelector(getEmojiValidationMap);

  return <CellAuthorWrapper {...props}>{renderContent(props, emojiValidationMap)}</CellAuthorWrapper>;
};

function renderContent(props: CellCommentLineProps, emojiValidationMap: EmojiMap) {
  return (
    <>
      <StyledCommentDiv
        $isSelected={Boolean(props.isSelected)}
        $isUnread={Boolean(props.isUnread)}
        $hasFollowingComments={Boolean(props.hasFollowingComments)}
        $isAnnotation={props.shouldUseEllipsisOverflow}
      >
        {props.shouldDisplayDraftStatus && renderDraftIconAndText(props)}
        {props.shouldDisplayAttachmentIcon && renderAttachmentIcon(props)}
        <StyledSummaryDiv
          $isSelected={Boolean(props.isSelected)}
          $isUnread={Boolean(props.isUnread)}
          $isAnnotation={props.shouldUseEllipsisOverflow}
          $hasFollowingComments={Boolean(props.hasFollowingComments)}
          $hasHighlight={Boolean(props.hasHighlight)}
        >
          {props.renderSummary({props, emojiValidationMap})}
        </StyledSummaryDiv>
      </StyledCommentDiv>
      {props.hasFollowingComments && renderFollowingCommentsMoreMarker(props)}
    </>
  );
}

function renderAttachmentIcon(props: CellCommentLineProps) {
  return (
    <StyledIconDiv
      $isSelected={Boolean(props.isSelected)}
      $isUnread={Boolean(props.isUnread)}
      $hasFollowingComments={Boolean(props.hasFollowingComments)}
      $isAnnotation={props.shouldUseEllipsisOverflow}
    >
      <Icon name="attachmentSmall" />
    </StyledIconDiv>
  );
}

function renderDraftIconAndText(props: CellCommentLineProps) {
  return (
    <>
      <StyledDraftIconDiv
        $isSelected={Boolean(props.isSelected)}
        $isUnread={Boolean(props.isUnread)}
        $hasFollowingComments={Boolean(props.hasFollowingComments)}
        $isAnnotation={props.shouldUseEllipsisOverflow}
      >
        <Icon name="draftFilledTiny" />
      </StyledDraftIconDiv>
      <StyledDraftTextDiv
        $isSelected={Boolean(props.isSelected)}
        $isUnread={Boolean(props.isUnread)}
        $hasFollowingComments={Boolean(props.hasFollowingComments)}
        $isAnnotation={props.shouldUseEllipsisOverflow}
      >
        <Trans id="cell-comment-draft">Draft</Trans>
      </StyledDraftTextDiv>
    </>
  );
}

function renderFollowingCommentsMoreMarker(props: CellCommentLineProps) {
  return (
    <StyledMoreDiv
      $isSelected={Boolean(props.isSelected)}
      $isUnread={Boolean(props.isUnread)}
      $hasFollowingComments={Boolean(props.hasFollowingComments)}
      $isAnnotation={props.shouldUseEllipsisOverflow}
    >
      <Icon name="plusTiny" />
      <StyledMoreTextDiv>
        <Trans id="cell-comment-more">more</Trans>
      </StyledMoreTextDiv>
    </StyledMoreDiv>
  );
}

export function renderSummaryText(
  trimmedText: string,
  emojiValidationMap: EmojiMap,
  isSelected: boolean | undefined,
  mentionableAliases: ReadonlySet<string> | undefined,
  isInline: boolean = false,
) {
  return (
    <UserThemeConsumer>
      {(theme) => {
        const mentionsColor = isSelected ? theme.greys.white : theme.palette.blue.shade40;
        const markdownTransforms = makeMarkdownTransforms(
          emojiValidationMap,
          mentionableAliases,
          mentionsColor,
        );
        return (
          <MarkdownPlainFormatter text={trimmedText} transform={markdownTransforms} isInline={isInline} />
        );
      }}
    </UserThemeConsumer>
  );
}

/*
 * Transform Mentions and Emojis in the text.
 */

const makeMarkdownTransforms = memoize(2)(
  (emojiValidationMap: EmojiMap, mentionableAliases?: ReadonlySet<string>, color?: string): NodeTransform =>
    flow(
      makeMarkdownTransformMentions(mentionableAliases || new Set(), color),
      makeMarkdownTransformEmojis(emojiValidationMap),
    ),
);
